import React, { useState, useRef, useEffect } from 'react';

const BeforeAfterSlider = ({ beforeImage, afterImage, beforeAlt = 'Vorher', afterAlt = 'Nachher' }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
      
      const handleResize = () => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.offsetWidth);
        }
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = (e) => {
    isDragging.current = true;
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseMove = (e) => {
    if (isDragging.current) {
      handleMove(e.clientX);
    }
  };

  const handleTouchStart = (e) => {
    isDragging.current = true;
    handleMove(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (isDragging.current) {
      handleMove(e.touches[0].clientX);
    }
  };

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16/10',
        overflow: 'hidden',
        borderRadius: '16px',
        cursor: 'ew-resize',
        userSelect: 'none',
        touchAction: 'pan-y'
      }}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleMouseUp}
    >
      {/* After Image (Background - Full width) */}
      <img
        src={afterImage}
        alt={afterAlt}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
      />
      
      {/* Before Image (Clipped to slider position) */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: `${sliderPosition}%`,
          height: '100%',
          overflow: 'hidden'
        }}
      >
        <img
          src={beforeImage}
          alt={beforeAlt}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: containerWidth > 0 ? `${containerWidth}px` : '100vw',
            height: '100%',
            objectFit: 'cover',
            maxWidth: 'none'
          }}
        />
      </div>
      
      {/* Slider Handle Line */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: `${sliderPosition}%`,
          width: '4px',
          background: 'white',
          transform: 'translateX(-50%)',
          boxShadow: '0 0 10px rgba(0,0,0,0.4)',
          zIndex: 10
        }}
      >
        {/* Handle Circle */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '52px',
            height: '52px',
            background: 'white',
            borderRadius: '50%',
            boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'grab'
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 8l4 4-4 4" />
            <path d="M6 8l-4 4 4 4" />
          </svg>
        </div>
      </div>
      
      {/* VORHER Label */}
      <div
        style={{
          position: 'absolute',
          top: '14px',
          left: '14px',
          background: 'rgba(0,0,0,0.75)',
          color: 'white',
          padding: '8px 14px',
          borderRadius: '8px',
          fontSize: '13px',
          fontWeight: '700',
          letterSpacing: '0.5px',
          zIndex: 5,
          pointerEvents: 'none'
        }}
      >
        VORHER
      </div>
      
      {/* NACHHER Label */}
      <div
        style={{
          position: 'absolute',
          top: '14px',
          right: '14px',
          background: 'rgba(40, 167, 69, 0.9)',
          color: 'white',
          padding: '8px 14px',
          borderRadius: '8px',
          fontSize: '13px',
          fontWeight: '700',
          letterSpacing: '0.5px',
          zIndex: 5,
          pointerEvents: 'none'
        }}
      >
        NACHHER
      </div>
      
      {/* Drag hint - shows briefly */}
      <div
        style={{
          position: 'absolute',
          bottom: '14px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.6)',
          color: 'white',
          padding: '6px 12px',
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: '600',
          zIndex: 5,
          pointerEvents: 'none',
          opacity: 0.8
        }}
      >
        ← Ziehen zum Vergleichen →
      </div>
    </div>
  );
};

export default BeforeAfterSlider;
